import {
  forwardRef,
  type AriaAttributes,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

import { useFieldContext } from './Field';

/**
 * Controles de entrada do Design System (ADR-0065), do `.input` do pacote: fundo
 * `surface`, borda hairline, cantos retos e cursor de texto em aço. No foco a borda
 * vira aço e o outline perde o offset — o controle já tem moldura própria, o offset
 * de 2px do resto da interface abriria um halo desnecessário.
 *
 * E165.F1.T08 (HTML 6.8, ADR-0114 §5): altura mínima 42px, respiro de 10px e texto de
 * 15px, como o `.field input` do molde.
 */
export const CLASSES_CONTROLE =
  'w-full min-h-[42px] border border-border bg-surface px-2.5 py-2 text-corpo text-fg caret-accent placeholder:text-fg-dim transition-colors duration-fast ease-sc hover:border-fg/45 focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent disabled:cursor-not-allowed disabled:bg-surface-3 disabled:opacity-60';

/** Aplica id e ARIA do `Field` mais próximo, sem sobrescrever o que veio por prop. */
function useControleField(props: {
  // `| undefined` explícito: o tsconfig usa `exactOptionalPropertyTypes`, e as
  // props de elemento chegam como `string | undefined`, não como ausentes.
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: AriaAttributes['aria-invalid'];
  'aria-required'?: AriaAttributes['aria-required'];
}) {
  const campo = useFieldContext();
  if (!campo) return {};
  return {
    id: props.id ?? campo.id,
    'aria-describedby': props['aria-describedby'] ?? campo.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (campo.invalid ? true : undefined),
    // `aria-required` e não o `required` nativo: o primitivo descreve, não muda
    // o comportamento de submit do formulário que já existe nas telas.
    'aria-required': props['aria-required'] ?? (campo.required ? true : undefined),
  };
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...props },
  ref,
) {
  const doField = useControleField(props);
  return <input ref={ref} className={cn(CLASSES_CONTROLE, className)} {...doField} {...props} />;
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, ...props },
  ref,
) {
  const doField = useControleField(props);
  return (
    <textarea
      ref={ref}
      className={cn(CLASSES_CONTROLE, 'min-h-[90px] resize-y', className)}
      {...doField}
      {...props}
    />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, ...props },
  ref,
) {
  const doField = useControleField(props);
  return <select ref={ref} className={cn(CLASSES_CONTROLE, className)} {...doField} {...props} />;
});
